import {
	ArrowDownOutlined,
	ArrowUpOutlined,
	MinusOutlined,
	TeamOutlined,
	UserAddOutlined,
	UserDeleteOutlined,
} from '@ant-design/icons';
import { dashboardApi } from 'Api/dashboard';
import { Input, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { Loader } from 'components/Global';
import DatePickerField from 'components/Global/DatePicker';
import SelectDropdown from 'components/Global/SelectDropdown';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Legend,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';
import { AppDispatch } from 'redux/store';
import { fetchPayPeriodAction } from '../../redux/actions/payPeriodAction';
import './index.scss';

const CHART_COLORS = {
	grossPay: '#584495',
	deductions: '#FF4D4F',
	netPay: '#52C41A',
};

const PIE_COLORS: Record<string, string> = {
	NAPSA: '#584495',
	NHIMA: '#7B68AE',
	PAYE: '#1890FF',
	'Mid-month': '#FAAD14',
	Other: '#13C2C2',
};

interface CardData {
	totalEmployees: number;
	employeesAdded: number;
	employeesRemoved: number;
	prevTotalEmployees: number;
	prevEmployeesAdded: number;
	prevEmployeesRemoved: number;
}

interface TrendItem {
	periodName: string;
	totalGrossPay: number;
	totalDeductions: number;
	totalNetPay: number;
}

interface DeductionItem {
	name: string;
	value: number;
}

interface EmployeeRow {
	employeeName: string;
	employeeId: string;
	department: string;
	branch: string;
	basicPay: number;
	totalEarnings: number;
	totalDeductions: number;
	netPay: number;
}

interface DashboardData {
	cards: CardData;
	payrollTrend: TrendItem[];
	deductionsBreakdown: DeductionItem[];
}

const formatCurrency = (value: number) =>
	new Intl.NumberFormat('en-ZM', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);

function shortenPeriodName(name: string): string {
	const match = name.match(/^(\w+)\s*\(/);
	if (match) {
		const monthStr = match[1];
		const yearMatch = name.match(/(\d{4})\)$/);
		const year = yearMatch ? yearMatch[1] : '';
		const short = monthStr.length > 3 ? monthStr.substring(0, 3) : monthStr;
		return year ? `${short} ${year}` : short;
	}
	return name.length > 10 ? name.substring(0, 10) + '...' : name;
}

const PayrollDashboard = () => {
	const dispatch = useDispatch<AppDispatch>();
	const { data: payPeriodOptions, isLoading: isPayPeriodLoading } = useSelector(
		(state: any) => state.payPeriod,
	);

	const [selectedYear, setSelectedYear] = useState<any>(dayjs());
	const [selectedPayPeriod, setSelectedPayPeriod] = useState<string>('');
	const [dashboardData, setDashboardData] = useState<DashboardData | null>(
		null,
	);
	const [isLoading, setIsLoading] = useState(false);
	const hasAutoSelected = useRef(false);

	useEffect(() => {
		const year = new Date(selectedYear).getFullYear();
		dispatch(fetchPayPeriodAction({ year }));
	}, [selectedYear, dispatch]);

	useEffect(() => {
		if (!payPeriodOptions?.length || hasAutoSelected.current) return;

		const today = new Date();
		const currentPeriod = payPeriodOptions.find(
			(opt: any) =>
				opt.value &&
				new Date(opt.startDate) <= today &&
				new Date(opt.endDate) >= today,
		);
		const fallback = payPeriodOptions.find((opt: any) => opt.value);
		const selected = currentPeriod?.value || fallback?.value || '';
		if (selected) {
			setSelectedPayPeriod(selected);
			hasAutoSelected.current = true;
		}
	}, [payPeriodOptions]);

	const fetchDashboard = useCallback(async () => {
		if (!selectedPayPeriod) return;
		setIsLoading(true);
		try {
			const response = await dashboardApi.getDashboardSummary({
				payPeriodId: selectedPayPeriod,
			});
			setDashboardData(response?.data);
		} catch {
			setDashboardData(null);
		} finally {
			setIsLoading(false);
		}
	}, [selectedPayPeriod]);

	useEffect(() => {
		fetchDashboard();
	}, [fetchDashboard]);

	const handleYearChange = (value: any) => {
		setSelectedYear(value);
		setSelectedPayPeriod('');
		setDashboardData(null);
		hasAutoSelected.current = false;
	};

	const handlePayPeriodChange = (value: string) => {
		setSelectedPayPeriod(String(value));
	};

	return (
		<div className="dashboard">
			<div className="dashboard__header">
				<h2 className="dashboard__title">Dashboard</h2>
				<div className="dashboard__filters">
					<div style={{ width: 160 }}>
						<DatePickerField
							name="year"
							value={selectedYear}
							label="Year"
							required={false}
							helperText=""
							onChange={handleYearChange}
							isError={false}
							picker="year"
						/>
					</div>
					<div style={{ width: 280 }}>
						<SelectDropdown
							placeholder="Select Pay Period"
							options={payPeriodOptions ?? []}
							value={selectedPayPeriod}
							disabled={isPayPeriodLoading}
							loading={isPayPeriodLoading}
							onChange={(val) => handlePayPeriodChange(String(val))}
							size="large"
							required={false}
							helperText=""
							label="Pay Period"
							isError={false}
						/>
					</div>
				</div>
			</div>

			{isLoading ? (
				<div
					className="dashboard__empty"
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '70vh',
					}}
				>
					<Loader />
				</div>
			) : !dashboardData ? (
				<div className="dashboard__empty">
					Select a pay period to view dashboard
				</div>
			) : (
				<>
					<KpiCards cards={dashboardData.cards} />
					<div className="dashboard__charts">
						<PayrollTrendChart data={dashboardData.payrollTrend} />
						<DeductionsPieChart data={dashboardData.deductionsBreakdown} />
					</div>
					<EmployeeTable payPeriodId={selectedPayPeriod} />
				</>
			)}
		</div>
	);
};

function KpiCards({ cards }: { cards: CardData }) {
	const items = [
		{
			label: 'Total Active Employees',
			value: cards.totalEmployees,
			prev: cards.prevTotalEmployees,
			color: 'purple' as const,
			icon: <TeamOutlined />,
		},
		{
			label: 'Employees Added',
			value: cards.employeesAdded,
			prev: cards.prevEmployeesAdded,
			color: 'green' as const,
			icon: <UserAddOutlined />,
		},
		{
			label: 'Employees Removed',
			value: cards.employeesRemoved,
			prev: cards.prevEmployeesRemoved,
			color: 'red' as const,
			icon: <UserDeleteOutlined />,
		},
	];

	return (
		<div className="dashboard__cards">
			{items.map((item) => {
				const diff = item.value - item.prev;
				const deltaClass = diff > 0 ? 'up' : diff < 0 ? 'down' : 'neutral';
				const DeltaIcon =
					diff > 0
						? ArrowUpOutlined
						: diff < 0
							? ArrowDownOutlined
							: MinusOutlined;

				return (
					<div
						key={item.label}
						className={`dashboard__card dashboard__card--${item.color}`}
					>
						<div className="dashboard__card-info">
							<span className="dashboard__card-label">{item.label}</span>
							<span className="dashboard__card-value">{item.value}</span>
							<span
								className={`dashboard__card-delta dashboard__card-delta--${deltaClass}`}
							>
								<DeltaIcon />
								{Math.abs(diff)} vs last pay period
							</span>
						</div>
						<div
							className={`dashboard__card-icon dashboard__card-icon--${item.color}`}
						>
							{item.icon}
						</div>
					</div>
				);
			})}
		</div>
	);
}

const BAR_KEYS = [
	{ key: 'totalGrossPay', name: 'Gross Pay', color: CHART_COLORS.grossPay },
	{
		key: 'totalDeductions',
		name: 'Deductions',
		color: CHART_COLORS.deductions,
	},
	{ key: 'totalNetPay', name: 'Net Pay', color: CHART_COLORS.netPay },
];

function PayrollTrendChart({ data }: { data: TrendItem[] }) {
	const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());

	if (!data?.length) {
		return (
			<div className="dashboard__chart-card">
				<div className="dashboard__chart-card-title">Payroll Overview</div>
				<div className="dashboard__empty">No payroll data</div>
			</div>
		);
	}

	const chartData = data.map((d) => ({
		...d,
		shortName: shortenPeriodName(d.periodName),
	}));

	const handleLegendClick = (entry: any) => {
		const { dataKey } = entry;
		setHiddenKeys((prev) => {
			const next = new Set(prev);
			if (next.has(dataKey)) {
				next.delete(dataKey);
			} else {
				next.add(dataKey);
			}
			return next;
		});
	};

	return (
		<div className="dashboard__chart-card">
			<div className="dashboard__chart-card-title">Payroll Overview</div>
			<ResponsiveContainer width="100%" height={300}>
				<BarChart data={chartData} barGap={4} barSize={20}>
					<CartesianGrid strokeDasharray="3 3" vertical={false} />
					<XAxis
						dataKey="shortName"
						tick={{ fontSize: 12 }}
						axisLine={false}
						tickLine={false}
					/>
					<YAxis
						tick={{ fontSize: 12 }}
						axisLine={false}
						tickLine={false}
						tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v)}
					/>
					<Tooltip
						formatter={(value: number, name: string) => [
							formatCurrency(value),
							name,
						]}
					/>
					<Legend
						wrapperStyle={{
							fontSize: 12,
							paddingTop: 12,
							cursor: 'pointer',
						}}
						onClick={handleLegendClick}
					/>
					{BAR_KEYS.map(({ key, name, color }) => (
						<Bar
							key={key}
							dataKey={key}
							name={name}
							fill={color}
							radius={[4, 4, 0, 0]}
							hide={hiddenKeys.has(key)}
						/>
					))}
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
}

function DeductionsPieChart({ data }: { data: DeductionItem[] }) {
	const [hiddenNames, setHiddenNames] = useState<Set<string>>(new Set());

	if (!data?.length) {
		return (
			<div className="dashboard__chart-card">
				<div className="dashboard__chart-card-title">Deductions Breakdown</div>
				<div className="dashboard__empty">No deductions data</div>
			</div>
		);
	}

	const visibleData = data.filter((d) => !hiddenNames.has(d.name));
	const total = visibleData.reduce((sum, d) => sum + d.value, 0);

	const handleLegendClick = (entry: any) => {
		const name = entry.value;
		setHiddenNames((prev) => {
			const next = new Set(prev);
			if (next.has(name)) {
				next.delete(name);
			} else {
				if (next.size < data.length - 1) {
					next.add(name);
				}
			}
			return next;
		});
	};

	const legendPayload = data.map((d) => ({
		value: d.name,
		type: 'circle' as const,
		color: hiddenNames.has(d.name)
			? '#ccc'
			: (PIE_COLORS[d.name] ?? PIE_COLORS['Other']),
		id: d.name,
	}));

	return (
		<div className="dashboard__chart-card">
			<div className="dashboard__chart-card-title">Deductions Breakdown</div>
			<ResponsiveContainer width="100%" height={300}>
				<PieChart>
					<Pie
						data={visibleData}
						cx="50%"
						cy="50%"
						innerRadius={60}
						outerRadius={100}
						dataKey="value"
						nameKey="name"
						label={({ name, value }) =>
							total > 0
								? `${name} ${((value / total) * 100).toFixed(1)}%`
								: name
						}
						labelLine={{ strokeWidth: 1 }}
					>
						{visibleData.map((entry, idx) => (
							<Cell
								key={idx}
								fill={PIE_COLORS[entry.name] ?? PIE_COLORS['Other']}
							/>
						))}
					</Pie>
					<Tooltip
						formatter={(value: number, name: string) => [
							formatCurrency(value),
							name,
						]}
					/>
					<Legend
						layout="vertical"
						align="right"
						verticalAlign="middle"
						wrapperStyle={{
							fontSize: 12,
							cursor: 'pointer',
						}}
						payload={legendPayload}
						onClick={handleLegendClick}
					/>
				</PieChart>
			</ResponsiveContainer>
		</div>
	);
}

function EmployeeTable({ payPeriodId }: { payPeriodId: string }) {
	const [data, setData] = useState<EmployeeRow[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [pageSize] = useState(10);
	const [search, setSearch] = useState('');
	const [sortField, setSortField] = useState('employeeName');
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
	const [loading, setLoading] = useState(false);
	const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const fetchEmployees = useCallback(async () => {
		if (!payPeriodId) return;
		setLoading(true);
		try {
			const response = await dashboardApi.getDashboardEmployees({
				payPeriodId,
				search,
				page,
				pageSize,
				sortField,
				sortOrder,
			});
			setData(response?.data?.data ?? []);
			setTotal(response?.data?.total ?? 0);
		} catch {
			setData([]);
			setTotal(0);
		} finally {
			setLoading(false);
		}
	}, [payPeriodId, search, page, pageSize, sortField, sortOrder]);

	useEffect(() => {
		fetchEmployees();
	}, [fetchEmployees]);

	useEffect(() => {
		setPage(1);
		setSearch('');
	}, [payPeriodId]);

	const handleSearchChange = (value: string) => {
		if (debounceTimer.current) clearTimeout(debounceTimer.current);
		debounceTimer.current = setTimeout(() => {
			setSearch(value);
			setPage(1);
		}, 300);
	};

	const handleTableChange = (
		pagination: TablePaginationConfig,
		_filters: any,
		sorter: SorterResult<EmployeeRow> | SorterResult<EmployeeRow>[],
	) => {
		if (pagination.current) setPage(pagination.current);

		const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
		if (singleSorter?.field) {
			setSortField(singleSorter.field as string);
			setSortOrder(singleSorter.order === 'descend' ? 'desc' : 'asc');
		}
	};

	const columns: ColumnsType<EmployeeRow> = [
		{
			title: 'Employee Name',
			dataIndex: 'employeeName',
			sorter: true,
			className: 'bg-white',
		},
		{
			title: 'Employee ID',
			dataIndex: 'employeeId',
			width: 130,
			sorter: true,
			className: 'bg-white',
		},
		{
			title: 'Branch',
			dataIndex: 'branch',
			sorter: true,
			className: 'bg-white',
		},
		{
			title: 'Department',
			dataIndex: 'department',
			sorter: true,
			className: 'bg-white',
		},
		{
			title: 'Basic Pay',
			dataIndex: 'basicPay',
			align: 'right',
			render: (v: number) => formatCurrency(v),
			className: 'bg-white',
		},
		{
			title: 'Total Earnings',
			dataIndex: 'totalEarnings',
			align: 'right',
			render: (v: number) => formatCurrency(v),
			className: 'bg-white',
		},
		{
			title: 'Total Deductions',
			dataIndex: 'totalDeductions',
			align: 'right',
			render: (v: number) => formatCurrency(v),
			className: 'bg-white',
		},
		{
			title: 'Net Pay',
			dataIndex: 'netPay',
			align: 'right',
			className: 'bg-white',
			render: (v: number) => (
				<span style={{ fontWeight: 600, color: '#52C41A' }}>
					{formatCurrency(v)}
				</span>
			),
		},
	];

	return (
		<div className="dashboard__table-card">
			<div className="dashboard__table-card-header">
				<span className="dashboard__table-card-title">
					Employee Payroll Summary
				</span>
				<Input.Search
					placeholder="Search by name, ID, branch, or department"
					onChange={(e) => handleSearchChange(e.target.value)}
					style={{ width: 320 }}
					allowClear
				/>
			</div>
			<Table
				columns={columns}
				dataSource={data}
				rowKey="employeeId"
				loading={loading}
				onChange={handleTableChange}
				pagination={{
					current: page,
					pageSize,
					total,
					showSizeChanger: false,
					showTotal: (t) => `Total ${t} employees`,
				}}
				scroll={{ x: 1050, y: 400 }}
				size="middle"
			/>
		</div>
	);
}

export default PayrollDashboard;
